/**
 * Domain utilities for custom subdomain routing.
 *
 * The base domain is derived from the current hostname at runtime,
 * so it works across all deployments (dev.metalark.ai, demo.metalark.ai,
 * app.metalark.ai, CloudFront distributions, etc.) without hardcoding.
 */

/**
 * Get the base domain for subdomain routing.
 * e.g. on dev.metalark.ai → 'dev.metalark.ai'
 * e.g. on acme.dev.metalark.ai → 'dev.metalark.ai'
 * e.g. on d1abc.cloudfront.net → 'cloudfront.net' (won't be used for subdomains)
 *
 * Falls back to the full hostname if it can't determine the base.
 */
export function getBaseDomain() {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  // localhost or IP — no base domain
  if (parts.length <= 1 || hostname === 'localhost') {
    return hostname;
  }

  // Two- or three-part hostnames are treated as the base domain — the
  // common deployments (dev.metalark.ai, demo.metalark.ai, app.metalark.ai)
  // and bare metalark.ai live here. A customer's preview URL on these
  // deployments is `<customer>.dev.metalark.ai`, not `<customer>.metalark.ai`,
  // so we want to keep the env prefix in the suffix shown to admins.
  if (parts.length <= 3) {
    return hostname;
  }

  // Four or more parts — assume the first segment is a customer / tenant
  // subdomain on top of a 3-part deployment domain (e.g.
  // acme.dev.metalark.ai → dev.metalark.ai). Drop only that one segment;
  // any further nesting stays intact.
  return parts.slice(1).join('.');
}

/**
 * Get the full subdomain URL for a given subdomain label.
 * e.g. getSubdomainUrl('acme') → 'https://acme.dev.metalark.ai'
 */
export function getSubdomainUrl(subdomain) {
  const base = getBaseDomain();
  return `https://${subdomain}.${base}`;
}

/**
 * Get the domain suffix for display in the UI.
 * e.g. '.dev.metalark.ai'
 */
export function getDomainSuffix() {
  return `.${getBaseDomain()}`;
}
